import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import AlertTypesService from "services/AdminConsole/Monitoring/AlertTypes/AlertTypesService";
import LookupService from "services/LookupService";
import AlertRulesService from "services/AdminConsole/Monitoring/AlertRules/AlertRulesService";
import AlertsService from "services/AdminConsole/Monitoring/Profiles/MonitoringProfilesService";
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiSpacer,
  EuiFieldText,
  EuiFieldNumber,
  EuiFormRow,
  EuiSkeletonText,
} from "@elastic/eui";
import { BellIcon, ObjectIcon, ResourcesIcon } from "components/Icons";
import { Roles, Subjects } from "settings/permissions";
import { Flyout } from "components/Flyout/Flyout";
import { FDPanelTitle } from "components/Title/FDPanelTitle";
import LinkToList from "components/Popover/LinkToList";
import Button from "components/Buttons/Button";
import Switch from "components/Buttons/Switch";
import Badge from "components/Badge/Badge";
import { badgeColors, badgeTextColors, defaultOperatorList } from "Consts";
import { isNullOrUndefined, isSubjectAuthorizedForUser } from "utils";
import {
  severityColors,
  defaultObjectSelection,
  levelsOptions,
  ALL,
  defaultSeverity,
  defaultChannel,
  noChannel,
} from "./AlertRulesConsts";
import { normalizeFormula } from "./AlertRulesUtils";
import { SelectDropdown } from "./SelectDropdown";
import CustomSelector from "./CustomSelector";
import MonitoringProfilesService from "services/AdminConsole/Monitoring/Profiles/MonitoringProfilesService";
import AlertObjectSelector from "../Profiles/components/Alerts/AlertObjectSelector";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { Card } from "components/Cards/Card";

export const AlertRulesDetailsFlyout = ({
  isNew,
  editMode,
  selectedItem,
  alert = null,
  profileOptions,
  onSave,
  onClose,
}: any) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [editObject, setEditObject] = useState<any>(null);
  const [isEditMode, setEditMode] = useState<boolean>(false);

  const [ruleName, setRuleName] = useState<string>("");
  const [isEnabled, setEnabled] = useState<boolean>(true);

  const [ignoreAlert, setIgnoreAlert] = useState<boolean>(false);
  const [selectedSeverity, setSelectedSeverity] = useState<string>(defaultSeverity);
  const [severityList, setSeverityList] = useState<any>([]);
  const [formula, setFormula] = useState<string>("");
  const [profileDefaultFormula, setProfileDefaultFormula] = useState<boolean>(false);

  const [operatorList, setOperatorList] = useState<any[]>(defaultOperatorList);

  const [editThreshold, setEditThreshold] = useState<any>([]);
  const [originalThreshold, setOriginalThreshold] = useState<any>([]);

  const [notificationChannels, setNotificationChannels] = useState<any[]>([]);

  const [filterScopeObject, setFilterScopeObject] = useState<any[]>([]);

  const [filterOpenObject, setFilterOpenObject] = useState<any[]>([defaultChannel]);
  const [filterClosedObject, setFilterClosedObject] = useState<any[]>([defaultChannel]);
  const [originalFilterOpenObject, setOriginalFilterOpenObject] = useState<any[]>([defaultChannel]);
  const [orifinalFilterClosedObject, setOriginalFilterClosedObject] = useState<any[]>([
    defaultChannel,
  ]);

  const [resources, setResources] = useState<any[]>([]);

  const [objectName, setObjectName] = useState<string>("");
  const [filterObjects, setFilterObjects] = useState<any[]>([]);
  const [objects, setObjects] = useState<any[]>([]);
  const [assignedObjects, setAssignedObjects] = useState<any[]>([]);

  const [isAlertLoading, setAlertLoading] = useState<boolean>(false);
  const [selectedAlertType, setSelectedAlertType] = useState<string>("");
  const [alertTypes, setAlertTypes] = useState<any[]>([]);

  const [alertTypeId, setAlertTypeId] = useState<string | null>(null);
  const [alertSubTypeId, setAlertSubTypeId] = useState<string | null>(null);
  const [alertResourceType, setAlertResourceType] = useState<string | null>(null);

  const [showNameError, setShowNameError] = useState<boolean>(false);
  const [showAlertTypeError, setShowAlertTypeError] = useState<boolean>(false);
  const [showScopeError, setShowScopeError] = useState<boolean>(false);

  const isFormulaUpdated = editThreshold?.every((i: any) => i?.value?.length > 0);
  const [levels, setLevels] = useState<any>(levelsOptions);
  const [selectedLevel, setSelectedLevel] = useState<string>("1");
  const { clientId } = useSelector(globalFilterSelector);
  const chosenAlertType = alertTypes?.find((i: any) => i.label === selectedAlertType) || "";

  const [originalRule, setOriginalRule] = useState<any>({
    name: "",
    alertType: "",
    scope: [],
    objects: [],
    severity: "",
    isEnabled: false,
    isAlertDisabled: false,
  });

  const editRule = {
    name: ruleName,
    alertType: selectedAlertType,
    scope: filterScopeObject?.map((i: any) => {
      const [_, value] = i.split(":");
      return value ? value.trim() : i;
    }),
    objects: filterObjects,
    severity: selectedSeverity,
    isEnabled,
    isAlertDisabled: ignoreAlert,
    level: selectedLevel === ALL ? null : `${selectedLevel}`,
  };

  const isEditModeEnabled = isSubjectAuthorizedForUser({
    subject: Subjects.Monitoring_Management,
    role: Roles.Admin,
  });

  const isSavingDisabled = () => {
    const isFormulaEnabled = editThreshold
      ?.map((i: any) => i.value)
      ?.some((i: any) => i?.length === 0);
    const isRestEnabled = [selectedSeverity, ...filterOpenObject, ...filterClosedObject]?.every(
      (i) => i?.includes("Default")
    );

    const isNewSaveDisabled = isNullOrUndefined(editThreshold)
      ? isRestEnabled
      : isRestEnabled && isFormulaEnabled;

    if (!isNew && editRule?.isAlertDisabled === true) {
      return JSON.stringify(originalRule) === JSON.stringify(editRule);
    }

    if (!isNew && editRule?.isAlertDisabled === false) {
      const original: any = {
        ...originalRule,
        t: originalThreshold ? originalThreshold : null,
        ncOpen: originalFilterOpenObject,
        ncClose: orifinalFilterClosedObject,
      };

      const editable: any = {
        ...editRule,
        t: isFormulaUpdated ? editThreshold : null,
        ncOpen: filterOpenObject,
        ncClose: filterClosedObject,
      };

      if (originalRule?.isAlertDisabled === true) {
        delete original.isAlertDisabled;
        delete editable.isAlertDisabled;
      }

      return JSON.stringify(original) === JSON.stringify(editable) || isNewSaveDisabled;
    }

    if (isNew) {
      if (editRule?.isAlertDisabled === true) return false;
      return isNewSaveDisabled;
    }
  };

  const handleClose = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setEditObject(null);
    onClose();
  };

  useEffect(() => {
    if (isNew) {
      setEditMode(true);
      setProfileDefaultFormula(true);
    }
    if (editMode) setEditMode(true);
  }, [isNew, editMode]);

  useEffect(() => {
    if (isNullOrUndefined(alert)) return;
    setSelectedSeverity(alert?.severityTypeName);
    setFilterScopeObject([`Resources: ${alert?.resourceName}`]);
    setFilterObjects(isNullOrUndefined(alert?.targetObjectName) ? [] : [alert?.targetObjectName]);
    setAssignedObjects(
      isNullOrUndefined(alert?.targetObjectName) ? [] : [{ name: alert?.targetObjectName }]
    );
  }, [alert]);

  const getNotificationChannels = async () => {
    try {
      const result = await AlertsService.getNotificationChannels({
        clientId,
      });
      if (result?.channels) {
        setNotificationChannels(
          result.channels.map((channel: any) => ({
            ...channel,
          }))
        );
      }
    } catch (error) {}
  };

  const getObjects = ({ ruleId, assignedAlerts, assignedProfiles, assignedResources }: any) => {
    const payload = {
      alertAssignment: {
        id: assignedAlerts?.id,
        subtypeId: assignedAlerts?.subtypeId,
      },
      monitoringProfileAssignments: assignedProfiles?.map((i: any) => ({ id: i.id })),
      resourceAssignments: assignedResources?.map((i: any) => ({ id: i.id })),
      ...(ruleId && {
        currentAssignment: {
          alertExceptionRuleId: ruleId,
        },
      }),
    };

    MonitoringProfilesService.getAlertObjects({ clientId, payload }).then((result) => {
      setObjects(result?.availableObjects || []);
      if (isNullOrUndefined(alert)) setAssignedObjects(result?.assignedObjects || []);
    });
  };

  const getAlertDefinition = ({ alertTypeId, alertSubtypeId, thresholds }: any) => {
    AlertTypesService.getAlertDefinition({
      clientId,
      alertTypeId,
      alertSubtypeId,
    }).then((result) => {
      setFormula(result?.formula || "");
      setObjectName(result?.objectName || "");
      setAlertTypeId(result?.alertTypeId);
      setAlertSubTypeId(result?.alertSubtypeId);
      if (isNullOrUndefined(thresholds)) {
        setEditThreshold(
          result?.alertThresholdTemplate?.values?.map((i: any) => ({
            ...i,
            value: i.key === "MessageRegexFilter" && isNullOrUndefined(i?.value) ? ".*" : i.value,
          }))
        );
      }
    });
  };

  const getSeverity = (severity: any) => {
    LookupService.getAlertSeverityTypes(clientId).then((result) => {
      setSeverityList(
        [
          { label: defaultSeverity, value: defaultSeverity },
          ...result?.data?.severityTypes?.map((i: any) => ({ label: i.name, ...i })),
        ].map((s) => {
          if (severity === s.label) return { ...s, checked: "on" };
          return { ...s, checked: "" };
        })
      );
    });
  };

  const getRule = useCallback(() => {
    setLoading(true);
    AlertRulesService.getRule({
      clientId,
      ruleId: selectedItem.id,
    }).then((result) => {
      setEditObject(result);
      setOriginalThreshold(result?.overrides?.threshold?.thresholdValues || null);
      setEditThreshold(result?.overrides?.threshold?.thresholdValues || null);
      if (isNullOrUndefined(result?.overrides?.threshold)) setProfileDefaultFormula(true);
      setRuleName(result?.name || "");
      setEnabled(result?.isEnabled || true);

      const currentSeverity = result?.overrides?.severity?.name || defaultSeverity;
      setSelectedSeverity(currentSeverity);

      setIgnoreAlert(result?.overrides?.isAlertDisabled);
      setSelectedAlertType(selectedItem?.alertType);

      const channelsOpenLength =
        result?.overrides?.alertOpenNotificationChannelAssignments?.channels?.length > 0;

      const channelsClosedLength =
        result?.overrides?.alertCloseNotificationChannelAssignments?.channels?.length > 0;

      const openChannels = channelsOpenLength
        ? result?.overrides?.alertOpenNotificationChannelAssignments?.channels?.map(
            (c: any) => `Channels: ${c.name}`
          )
        : result?.overrides?.alertOpenNotificationChannelAssignments?.channels
          ? [noChannel]
          : [defaultChannel];

      const closedChannels = channelsClosedLength
        ? result?.overrides?.alertCloseNotificationChannelAssignments?.channels.map(
            (c: any) => `Channels: ${c.name}`
          )
        : result?.overrides?.alertCloseNotificationChannelAssignments?.channels
          ? [noChannel]
          : [defaultChannel];

      setFilterOpenObject(openChannels);
      setFilterClosedObject(closedChannels);

      setOriginalFilterOpenObject(openChannels);
      setOriginalFilterClosedObject(closedChannels);

      const assignedAlerts = result?.scope?.alertAssignment;
      const assignedResources = result?.scope?.resourceAssignments || [];
      const assignedProfiles = result?.scope?.monitoringProfileAssignments || [];
      const assignedObjects = result?.scope?.objectAssignments || [];

      setSelectedLevel(result?.scope?.alertAssignment?.level || ALL);

      const mappedLevels = levels.map((i: any) => {
        if (`${i.value}` === `${result?.scope?.alertAssignment?.level}`) {
          return { ...i, checked: "on" };
        }
        if (isFormulaUpdated && i.label === ALL) return { ...i, disabled: true, checked: "" };
        return { ...i, disabled: false };
      });
      setLevels(mappedLevels);

      if (assignedResources?.length > 0) {
        setFilterScopeObject(assignedResources?.map((i: any) => `Resources: ${i.name}`));
      }
      if (assignedProfiles?.length > 0) {
        setFilterScopeObject(assignedProfiles?.map((i: any) => `Profiles: ${i.name}`));
      }

      setOriginalRule({
        name: result?.name || "",
        alertType: selectedItem?.alertType,
        scope: [...assignedResources, ...assignedProfiles]?.map((s) => s.name),
        objects: assignedObjects?.map((o: any) => o.name),
        severity: result?.overrides?.severity?.name || defaultSeverity,
        isEnabled: result?.isEnabled,
        isAlertDisabled: result?.overrides?.isAlertDisabled,
        level: result?.scope?.alertAssignment?.level
          ? `${result?.scope?.alertAssignment?.level}`
          : null,
      });

      setFilterObjects(assignedObjects?.map((i: any) => i.name));

      getAlertDefinition({
        alertTypeId: result?.scope?.alertAssignment?.id,
        alertSubtypeId: result?.scope?.alertAssignment?.subtypeId,
        thresholds: result?.overrides?.threshold?.thresholdValues || null,
      });

      getObjects({ ruleId: selectedItem.id, assignedAlerts, assignedProfiles, assignedResources });

      getSeverity(currentSeverity);
      setLoading(false);
    });

    getNotificationChannels();
  }, [clientId, selectedItem]);

  useEffect(() => {
    if (!isNew) getRule();
    if (isNew) getSeverity(defaultSeverity);

    LookupService.getResourcesV2(clientId).then((result) => {
      setResources(result?.data);
    });

    setAlertLoading(true);
    AlertTypesService.getAlertTypesOverview({ clientId, filters: {} }).then((response) => {
      if (response) {
        if (isNew && isNullOrUndefined(alert)) {
          setSelectedAlertType(
            response?.data?.alertTypes?.length > 0 ? response?.data?.alertTypes[0]?.name : ""
          );
        } else if (isNew && !isNullOrUndefined(alert)) {
          setSelectedAlertType(alert?.alertTypeName);
        }
        setAlertTypes(response?.data?.alertTypes?.map((i: any) => ({ label: i.name, ...i })));
      }
      setAlertLoading(false);
    });
  }, [clientId, isEditMode]);

  useEffect(() => {
    if (!isNullOrUndefined(chosenAlertType)) setAlertResourceType(chosenAlertType?.resourceType);

    if (isNew && selectedAlertType) {
      const isProfile = filterScopeObject?.some((i) => i?.includes("Profiles:"));

      const scopeData = isProfile
        ? profileOptions
        : resources?.filter((r) => r.resourceType === chosenAlertType?.resourceType);

      const scopeValue = scopeData?.filter(
        (i: any) => filterScopeObject?.filter((n) => n?.includes(i.name)).length > 0
      );

      getAlertDefinition({
        alertTypeId: chosenAlertType?.id,
        alertSubtypeId: chosenAlertType?.subtypeId,
        thresholds: null,
      });

      getObjects({
        ruleId: null,
        assignedAlerts: chosenAlertType,
        assignedProfiles: isProfile ? scopeValue : [],
        assignedResources: isProfile ? [] : scopeValue,
      });

      getNotificationChannels();
    }
  }, [selectedAlertType, filterScopeObject, resources, chosenAlertType]);

  useEffect(() => {
    const autoLevel = isFormulaUpdated
      ? selectedLevel === ALL
        ? "1"
        : selectedLevel
      : selectedLevel;

    setSelectedLevel(autoLevel);

    setLevels(
      levels?.map((i: any) => {
        if (isFormulaUpdated && i.label === ALL) return { ...i, disabled: true, checked: "" };
        return { ...i, disabled: false, checked: i.label === `${autoLevel}` ? "on" : "" };
      })
    );
  }, [isFormulaUpdated]);

  const handleSelectedNotification = (type: string) => {
    const selectedNotification = type === "Open" ? filterOpenObject : filterClosedObject;

    if (selectedNotification?.includes("Profile Default Channels")) return null;
    if (selectedNotification?.includes("No Notifications")) return { channels: [] };

    const notificationIds = notificationChannels
      .filter((i) => {
        return (
          selectedNotification.filter((n) => {
            const [_, name] = n?.split(":");

            return name?.trim() === i.name;
          }).length > 0
        );
      })
      .map((i) => ({ id: i.id }));

    return {
      channels: notificationIds,
    };
  };

  const validateRequiredFields = () => {
    if (ruleName?.length === 0) {
      setShowNameError(true);
      return true;
    }
    if (selectedAlertType?.length === 0) {
      setShowAlertTypeError(true);
      return true;
    }
    if (filterScopeObject?.length === 0) {
      setShowScopeError(true);
      return true;
    }
    return false;
  };

  const handleSave = () => {
    const isInvalid = validateRequiredFields();
    if (isInvalid) return;

    const severity = severityList?.find((i: any) => i.name === selectedSeverity);
    const scopeTypesProfile = "Profiles:";

    const isProfile = filterScopeObject.some((i) => i?.includes(scopeTypesProfile));

    const scopeData = isProfile
      ? profileOptions
      : resources.filter((r) => r.resourceType === alertResourceType);

    const scopeValue = scopeData
      .filter((i: any) => filterScopeObject.filter((n) => n?.includes(i.name)).length > 0)
      .map((i: any) => ({ id: i.id }));

    const payload = {
      name: ruleName,
      isEnabled: isEnabled,
      overrides: {
        isAlertDisabled: ignoreAlert, // id it false the overrides is required
        severity: ignoreAlert
          ? null
          : severity && severity?.id
            ? {
                id: severity.id,
              }
            : null,
        alertOpenNotificationChannelAssignments: ignoreAlert
          ? null
          : handleSelectedNotification("Open"),
        alertCloseNotificationChannelAssignments: ignoreAlert
          ? null
          : handleSelectedNotification("Closed"),
        threshold: ignoreAlert
          ? null
          : editThreshold?.length > 0 && editThreshold.every((i: any) => i?.value?.length > 0)
            ? {
                thresholdValues: editThreshold,
              }
            : null,
      },
      scope: {
        alertAssignment: {
          id: isNew ? alertTypeId : editObject?.scope?.alertAssignment?.id,
          subtypeId: isNew ? alertSubTypeId : editObject?.scope?.alertAssignment?.subtypeId,
          level:
            formula && formula?.includes("$")
              ? selectedLevel === ALL
                ? null
                : parseInt(selectedLevel, 10)
              : null,
        },
        monitoringProfileAssignments: isProfile ? scopeValue : [],
        resourceAssignments: isProfile ? [] : scopeValue,
        objectAssignments: filterObjects?.includes(defaultObjectSelection)
          ? null
          : filterObjects.map((i) => ({ name: i })),
      },
    };
    //debug for issue with saving

    setSaving(true);

    const params = isNew ? { clientId, payload } : { clientId, ruleId: editObject.id, payload };

    onSave(params, () => {
      setSaving(false);
    });
  };

  const viewChildren = (
    <EuiFlexGroup direction="column" gutterSize="l">
      <EuiFlexItem>
        <FDPanelTitle title={"Rule Details"} />

        <EuiSpacer size="m" />
        <EuiFlexGroup gutterSize="l">
          <EuiFlexItem>
            <EuiText size="s">
              <b style={{ color: "var(--text)" }}>Name:</b> &nbsp; {selectedItem?.name}
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size="s">
              <b style={{ color: "var(--text)" }}>Status:</b> &nbsp;{" "}
              <Badge
                text={selectedItem?.isEnabled ? "Enabled" : "Disabled"}
                color={selectedItem?.isEnabled ? badgeColors.$green : badgeColors.$red}
                style={{
                  color: selectedItem?.isEnabled ? badgeTextColors.$green : badgeTextColors.$red,
                  border: `1px solid ${selectedItem?.isEnabled ? badgeTextColors.$green : badgeTextColors.$red}`,
                  backgroundColor: "transparent",
                }}
              />
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="m" />
        <EuiFlexGroup gutterSize="l">
          <EuiFlexItem>
            <EuiText size="s">
              <b style={{ color: "var(--text)" }}>Alert Type:</b> &nbsp; {selectedItem?.alertType}
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="m" />
        <EuiFlexGroup gutterSize="l">
          <EuiFlexItem>
            <EuiText size="s">
              <b style={{ color: "var(--text)" }}>Scope:</b> &nbsp;{" "}
              <LinkToList
                ButtonTitle={
                  <span style={{ display: "flex", alignItems: "center", color: "var(--text)" }}>
                    <ResourcesIcon fill="#FF7E62" />
                    <span style={{ marginLeft: "8px" }}>
                      {selectedItem?.scope?.resourceAssignments?.length > 0
                        ? selectedItem?.scope?.resourceAssignments?.length
                        : selectedItem?.scope?.monitoringProfileAssignments?.length}
                    </span>
                  </span>
                }
                Columns={[
                  {
                    field: "name",
                    name:
                      selectedItem?.scope?.resourceAssignments?.length > 0
                        ? "Resources"
                        : "Profiles",
                  },
                ]}
                Items={
                  selectedItem?.scope?.resourceAssignments?.length > 0
                    ? selectedItem?.scope?.resourceAssignments
                    : selectedItem?.scope?.monitoringProfileAssignments
                }
                PopOverMaxWidth={300}
                Copy={false}
                showHeaders={true}
              />
            </EuiText>
          </EuiFlexItem>

          <EuiFlexItem>
            <EuiText size="s">
              <b style={{ color: "var(--text)" }}>Level:</b> &nbsp;
              <Badge
                text={selectedItem?.scope?.alertAssignment?.level || "All"}
                style={{
                  color: "var(--primary-color)",
                  border: "1px solid var(--primary-color)",
                  backgroundColor: "transparent",
                  width: "30px",
                  padding: 0,
                  display: "inline-flex",
                  justifyContent: "center",
                }}
              />
            </EuiText>
          </EuiFlexItem>

          <EuiFlexItem>
            <EuiText size="s">
              <b style={{ color: "var(--text)" }}>Objects:</b> &nbsp;{" "}
              <LinkToList
                ButtonTitle={
                  <span style={{ display: "flex", alignItems: "center", color: "var(--text)" }}>
                    <ObjectIcon fill="var(--violet)" />
                    <span style={{ marginLeft: "8px" }}>{selectedItem?.objects?.length}</span>
                  </span>
                }
                Columns={[
                  {
                    field: "name",
                    name: "Objects",
                  },
                ]}
                Items={selectedItem?.objects}
                PopOverMaxWidth={300}
                Copy={false}
                showHeaders={true}
              />
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="m" />
        <EuiFlexGroup gutterSize="l">
          <EuiFlexItem>
            <EuiText size="s">
              <b style={{ color: "var(--text)" }}>All Future Objects:</b> &nbsp;{" "}
              {selectedItem?.objects?.length > 0 ? "No" : "Yes"}
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>

      <div style={{ borderBottom: "1px solid var(--cyan)" }}></div>

      <EuiFlexItem>
        <FDPanelTitle title="Rule Behavior" />
        <EuiSpacer size="xs" />
        {!selectedItem?.ignoreAlert && (
          <EuiText size="s">The following values have been changed from their defaults:</EuiText>
        )}
        {selectedItem?.ignoreAlert && (
          <EuiText size="s">Alerts matching this rule will not fire.</EuiText>
        )}

        {!selectedItem?.ignoreAlert && isFormulaUpdated && (
          <>
            <EuiSpacer size="l" />
            <EuiFlexItem>
              <EuiText size="s">
                <b style={{ color: "var(--text)" }}>Tolerance Formula:</b> &nbsp;
                {normalizeFormula(formula, editThreshold)}
              </EuiText>
            </EuiFlexItem>
          </>
        )}

        <EuiSpacer size={!selectedItem?.ignoreAlert && isFormulaUpdated ? "m" : "l"} />

        {editObject?.overrides?.severity && (
          <EuiFlexItem>
            <EuiText size="s">
              <b style={{ color: "var(--text)" }}>Severity:</b> &nbsp;
              <Badge
                text={editObject?.overrides?.severity?.name}
                color={severityColors[editObject?.overrides?.severity?.name]}
                style={{ color: "#343741" }}
              />
            </EuiText>
          </EuiFlexItem>
        )}
        <EuiSpacer size="m" />
        <EuiFlexItem>
          {editObject?.overrides?.alertOpenNotificationChannelAssignments?.channels && (
            <EuiFlexGroup justifyContent="spaceAround">
              <EuiFlexItem grow={1}>
                <b style={{ color: "var(--text)" }}>Open Event Notifications:</b>
              </EuiFlexItem>
              <EuiFlexItem grow={1}>
                <EuiText size="s">
                  <LinkToList
                    ButtonTitle={
                      <span style={{ display: "flex", alignItems: "center", color: "var(--text)" }}>
                        <BellIcon fill={"#D36086"} />
                        <span style={{ marginLeft: "8px" }}>
                          {
                            editObject?.overrides?.alertOpenNotificationChannelAssignments?.channels
                              ?.length
                          }
                        </span>
                      </span>
                    }
                    Columns={[
                      {
                        field: "name",
                        name: "Name",
                      },
                    ]}
                    Items={editObject?.overrides?.alertOpenNotificationChannelAssignments?.channels}
                    PopOverMaxWidth={300}
                    Copy={false}
                    showHeaders={true}
                    enableSearch={false}
                  />
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          )}
          <EuiSpacer size="m" />
          {editObject?.overrides?.alertCloseNotificationChannelAssignments?.channels && (
            <EuiFlexGroup justifyContent="spaceAround">
              <EuiFlexItem grow={1}>
                <b style={{ color: "var(--text)" }}>Closed Event Notifications:</b>
              </EuiFlexItem>
              <EuiFlexItem grow={1}>
                <EuiText size="s">
                  <LinkToList
                    ButtonTitle={
                      <span style={{ display: "flex", alignItems: "center", color: "var(--text)" }}>
                        <BellIcon fill={"#D36086"} />
                        <span style={{ marginLeft: "8px" }}>
                          {
                            editObject?.overrides?.alertCloseNotificationChannelAssignments
                              ?.channels?.length
                          }
                        </span>
                      </span>
                    }
                    Columns={[
                      {
                        field: "name",
                        name: "Name",
                      },
                    ]}
                    Items={
                      editObject?.overrides?.alertCloseNotificationChannelAssignments?.channels
                    }
                    PopOverMaxWidth={300}
                    Copy={false}
                    showHeaders={true}
                    enableSearch={false}
                  />
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          )}
        </EuiFlexItem>
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const editChildren = (
    <EuiFlexGroup direction="column" gutterSize="l">
      <EuiFlexItem>
        <EuiFlexGroup gutterSize="l" alignItems="center">
          <EuiFlexItem grow={3}></EuiFlexItem>
          <EuiFlexItem>
            <Switch label="Enabled" checked={isEnabled} onChange={() => setEnabled(!isEnabled)} />
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiFlexGroup gutterSize="l" alignItems="center">
          <EuiFlexItem grow={3}>
            <EuiFormRow
              label={"Name:"}
              fullWidth
              className={"row-required"}
              error="Name is required"
              isInvalid={showNameError}>
              <EuiFieldText
                id={"threshold_name"}
                min={0}
                placeholder={ruleName?.length === 0 ? "Enter name" : ruleName}
                onChange={(e) => {
                  setRuleName(e.target.value);
                  setShowNameError(false);
                }}
                value={ruleName}
                fullWidth
                style={{ width: "100%" }}
              />
            </EuiFormRow>
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size="l" />

        <EuiFlexGroup gutterSize="l">
          <EuiFlexItem>
            {!isNew && (
              <EuiText size="s">
                <b>Alert Type:</b> &nbsp; {selectedItem?.alertType}
              </EuiText>
            )}

            {isNew && (
              <EuiFormRow
                label={"Alert Type:"}
                fullWidth
                className={"row-required"}
                error="Alert Type is required"
                isInvalid={showAlertTypeError}>
                <SelectDropdown
                  width={701}
                  selectedItem={selectedAlertType}
                  options={alertTypes}
                  setOptions={setAlertTypes}
                  setSelectedItem={(v: any) => {
                    setSelectedAlertType(v);
                    setShowAlertTypeError(false);
                  }}
                  onChange={() => {}}
                  isLoading={isAlertLoading}
                />
              </EuiFormRow>
            )}
          </EuiFlexItem>
        </EuiFlexGroup>
        <EuiSpacer size="l" />
        <EuiFlexGroup gutterSize="l" direction="column">
          <EuiFlexItem>
            <EuiFormRow
              label={"Scope:"}
              fullWidth
              className={"row-required"}
              error="Scope is required"
              isInvalid={showScopeError}>
              <CustomSelector
                width="701px"
                Items={[
                  {
                    display: "Profiles",
                    name: "Profiles",
                    value: "Profiles",
                    id: 0,
                    values: profileOptions
                      ?.map((i: any) => ({ name: i.name, value: i.name, id: i.id }))
                      .filter((i: any) => !i.name?.includes("System Alerts")),
                  },
                  ...(resources?.filter((i) =>
                    alertResourceType
                      ? i.resourceType === alertResourceType
                      : i.resourceType === "Instance"
                  )?.length > 0
                    ? [
                        {
                          display: "Resources",
                          name: "Resources",
                          value: "Resources",
                          id: 1,
                          values: resources
                            ?.filter((i) =>
                              alertResourceType
                                ? i.resourceType === alertResourceType
                                : i.resourceType === "Instance"
                            )
                            ?.map((i) => ({ name: i.name, value: i.name, id: i.id })),
                        },
                      ]
                    : []),
                ]}
                Name={"Scope"}
                Field={"scope"}
                filterObject={filterScopeObject}
                setFilterObject={(obj: any) => {
                  setFilterScopeObject(obj);
                  setShowScopeError(false);
                }}
                anchorPosition="downCenter"
              />
            </EuiFormRow>
          </EuiFlexItem>

          {formula && formula?.includes("$") && (
            <EuiFlexItem>
              <EuiFormRow label={"Level:"} fullWidth>
                <SelectDropdown
                  searchable={false}
                  selectedItem={selectedLevel}
                  options={levels}
                  setOptions={setLevels}
                  setSelectedItem={(v: any) => {
                    setSelectedLevel(v);
                  }}
                  onChange={() => {}}
                  isLoading={false}
                />
              </EuiFormRow>
            </EuiFlexItem>
          )}

          {!isNew && objectName && filterScopeObject?.length > 0 && (
            <EuiFlexItem>
              <EuiFormRow label={"Objects:"} fullWidth>
                <AlertObjectSelector
                  alert={{
                    ...chosenAlertType,
                    objectAssignments: assignedObjects,
                    selectedObjectNames: assignedObjects?.map((i) => i.name),
                  }}
                  clientId={clientId}
                  monitoringProfileId={0}
                  onSelectionChange={setFilterObjects}
                  objectTypeName={objectName}
                  objectOptions={objects}
                />
              </EuiFormRow>
            </EuiFlexItem>
          )}

          {isNew && filterScopeObject?.length > 0 && (
            <EuiFlexItem>
              <EuiFormRow label={"Objects:"} fullWidth>
                <AlertObjectSelector
                  alert={{
                    ...chosenAlertType,
                    ...(!isNullOrUndefined(alert) && { objectAssignments: assignedObjects }),
                    ...(!isNullOrUndefined(alert) && {
                      selectedObjectNames: assignedObjects?.map((i) => i.name),
                    }),
                  }}
                  clientId={clientId}
                  monitoringProfileId={0}
                  onSelectionChange={setFilterObjects}
                  objectTypeName={objectName}
                  objectOptions={objects}
                />
              </EuiFormRow>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>

      <div style={{ borderBottom: "1px solid var(--cyan)" }}></div>

      <EuiFlexItem>
        <EuiFlexGroup gutterSize="l" alignItems="center">
          <EuiFlexItem grow={3}>
            <FDPanelTitle title="Enter Rule Behavior" />
          </EuiFlexItem>
          <EuiFlexItem>
            <Switch
              label="Ignore Alert"
              checked={ignoreAlert}
              onChange={() => {
                setIgnoreAlert(!ignoreAlert);
              }}
            />
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size="m" />
        <EuiText size="s">
          Make a change to at least one of the following options to complete rule:
        </EuiText>

        <EuiSpacer size="l" />
        {!ignoreAlert && (
          <EuiFlexGroup gutterSize="l" direction="column">
            <EuiFlexItem>
              <EuiFormRow label={"Severity:"} fullWidth>
                <SelectDropdown
                  searchable={false}
                  selectedItem={selectedSeverity}
                  options={severityList}
                  setOptions={setSeverityList}
                  setSelectedItem={(v: any) => {
                    setSelectedSeverity(v);
                  }}
                  onChange={() => {}}
                  isLoading={false}
                />
              </EuiFormRow>
            </EuiFlexItem>

            <EuiFlexItem>
              <EuiSkeletonText
                lines={3}
                size={"xs"}
                isLoading={isLoading}
                contentAriaLabel="Loading text"
              />
            </EuiFlexItem>

            {!isLoading && formula && formula?.includes("$") && (
              <EuiFlexItem>
                <EuiFormRow label={"Tolerance Formula:"} fullWidth>
                  <>
                    <div style={{ textAlign: "right", padding: "10px", marginTop: "-30px" }}>
                      <Switch
                        label="Profile Default"
                        checked={profileDefaultFormula}
                        onChange={() => {
                          setProfileDefaultFormula(!profileDefaultFormula);
                          if (profileDefaultFormula === false) {
                            setEditThreshold(
                              editThreshold?.map((i: any) => {
                                return {
                                  ...i,
                                  value: i.key === "MessageRegexFilter" ? ".*" : "",
                                };
                              })
                            );
                          }
                        }}
                      />
                    </div>
                    {!profileDefaultFormula && (
                      <Card
                        borderColor="var(--blue)"
                        children={
                          <EuiText size="s">
                            {!profileDefaultFormula &&
                              formula?.split(" ")?.map((i: any) => {
                                const match = editThreshold?.find((v: any) => i?.includes(v.key));
                                if (isNullOrUndefined(match)) return <span>{`${i} `}</span>; //space is required
                                return (
                                  <>
                                    {i !== "$Operator" && i !== "$MessageRegexFilter" && (
                                      <div
                                        style={{
                                          width: "120px",
                                          display: "inline-block",
                                          paddingRight: "8px",
                                        }}>
                                        <EuiFieldNumber
                                          id={i.key}
                                          min={0}
                                          placeholder={i.value}
                                          onChange={(e) => {
                                            setEditThreshold(
                                              editThreshold?.map((i: any) => {
                                                if (i.key === match.key) {
                                                  return {
                                                    ...i,
                                                    value: e.target.value,
                                                  };
                                                }
                                                return i;
                                              })
                                            );
                                          }}
                                          value={match.value}
                                          fullWidth
                                        />
                                      </div>
                                    )}
                                    {i === "$Operator" && (
                                      <span
                                        style={{
                                          width: "80px",
                                          display: "inline-block",
                                          padding: "0px 4px",
                                        }}>
                                        <SelectDropdown
                                          width={80}
                                          isPanelWidth
                                          searchable={false}
                                          selectedItem={match.value}
                                          options={operatorList}
                                          setOptions={setOperatorList}
                                          setSelectedItem={(v: any) => {
                                            setEditThreshold(
                                              editThreshold?.map((i: any) => {
                                                if (i.key === match.key) {
                                                  return {
                                                    ...i,
                                                    value: v,
                                                  };
                                                }
                                                return i;
                                              })
                                            );
                                          }}
                                          onChange={() => {}}
                                          isLoading={false}
                                        />
                                      </span>
                                    )}

                                    {i === "$MessageRegexFilter" && (
                                      <span
                                        style={{
                                          width: "140px",
                                          display: "inline-block",
                                          paddingRight: "8px",
                                        }}>
                                        <EuiFieldText
                                          id={i.key}
                                          min={0}
                                          placeholder={i.value}
                                          onChange={(e: any) => {
                                            setEditThreshold(
                                              editThreshold?.map((i: any) => {
                                                if (i.key === match.key) {
                                                  return {
                                                    ...i,
                                                    value: e.target.value,
                                                  };
                                                }
                                                return i;
                                              })
                                            );
                                          }}
                                          value={match.value}
                                        />
                                      </span>
                                    )}
                                  </>
                                );
                              })}
                          </EuiText>
                        }
                      />
                    )}
                  </>
                </EuiFormRow>
              </EuiFlexItem>
            )}

            <EuiFlexItem>
              <EuiFormRow label={"Notify on Open Events:"} fullWidth>
                <CustomSelector
                  width="701px"
                  Items={[
                    {
                      display: "Profile Default Channels",
                      name: "Profile Default Channels",
                      value: "Profile Default Channels",
                      id: 0,
                    },
                    {
                      display: "No Notifications",
                      name: "No Notifications",
                      value: "No Notifications",
                      id: 1,
                    },
                    {
                      display: "Channels",
                      name: "Channels",
                      value: "Channels",
                      id: 2,
                      values: notificationChannels.map((i) => ({
                        name: i.type,
                        value: i.name,
                        type: i.type,
                      })),
                    },
                  ]}
                  Name={"Channels"}
                  Field={"channels"}
                  filterObject={filterOpenObject}
                  setFilterObject={setFilterOpenObject}
                  disableSorting
                />
              </EuiFormRow>
            </EuiFlexItem>

            <EuiFlexItem>
              <EuiFormRow label={"Notify on Closed Events:"} fullWidth>
                <CustomSelector
                  width="701px"
                  Items={[
                    {
                      display: "Profile Default Channels",
                      name: "Profile Default Channels",
                      value: "Profile Default Channels",
                      id: 0,
                    },
                    {
                      display: "No Notifications",
                      name: "No Notifications",
                      value: "No Notifications",
                      id: 1,
                    },
                    {
                      display: "Channels",
                      name: "Channels",
                      value: "Channels",
                      id: 2,
                      values: notificationChannels.map((i) => ({
                        name: i.type,
                        value: i.name,
                        type: i.type,
                      })),
                    },
                  ]}
                  Name={"Channels"}
                  Field={"channels"}
                  filterObject={filterClosedObject}
                  setFilterObject={setFilterClosedObject}
                  disableSorting
                />
              </EuiFormRow>
            </EuiFlexItem>
          </EuiFlexGroup>
        )}
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  return (
    <Flyout
      title={isNew ? "New Rule" : isEditMode ? "Edit Rule" : "View Rule"}
      onClose={handleClose}
      size="750px"
      children={isEditMode ? editChildren : viewChildren}
      footerChildren={
        <>
          {!isEditMode && (
            <EuiFlexGroup justifyContent="flexEnd">
              {isEditModeEnabled && (
                <EuiFlexItem grow={false}>
                  <Button
                    type="secondary"
                    text={"Edit"}
                    onClick={() => {
                      setEditMode(true);
                    }}
                  />
                </EuiFlexItem>
              )}

              <EuiFlexItem grow={false}>
                <Button type="primary" text={"Close"} onClick={handleClose} />
              </EuiFlexItem>
            </EuiFlexGroup>
          )}

          {isEditMode && (
            <EuiFlexGroup justifyContent="flexEnd">
              <EuiFlexItem grow={false}>
                <Button
                  type="secondary"
                  text={"Cancel"}
                  onClick={(e) => {
                    setEditMode(false);
                    handleClose(e);
                  }}
                />
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <Button
                  type="primary"
                  text={"Save"}
                  isLoading={isSaving}
                  onClick={handleSave}
                  disabled={isSavingDisabled() || isAlertLoading}
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          )}
        </>
      }
    />
  );
};
